import React, { useRef, useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useIMask } from 'react-imask';

import { useStateBasedOnProps } from 'utils/helper-hooks';

import HelpTooltip from 'components/help-tooltip';

const FieldDefault = ({ type = 'text', value = '', id, name, placeholder, label, labelHelp, darkLabel, largeLabel, required, replacer, triggerAsEvent, blurOnEnter, autoFocus, disabled, shortField, error, small, large, readonly, sideAction, passwordToggleable, addon, addonTransparent, mask, provideController, onChange, onFocus, onBlur, onKeyDown, onClick, onInput, onPaste, onSideActionClick, addonClick, children }) => {
    const { ref: inputElement, maskRef } = useIMask({ mask });
    const [valueState, setValueState] = useStateBasedOnProps(value);
    const [focused, setFocused] = useState(false);
    const [passwordVisible, setPasswordVisible] = useState(false);
    const { t } = useTranslation('common');

    const populated = useMemo(() => valueState !== '', [valueState]);

    const labelElement = useMemo(() => {
        if (!label) {
            return null;
        }

        return (
            <label htmlFor={id} className={'field-label' + (darkLabel ? ' dark' : '') + (largeLabel ? ' large' : '')}>
                { label }

                { required ? <span className="req"> *</span> : null }

                { labelHelp ? <HelpTooltip>{labelHelp}</HelpTooltip> : null }
            </label>
        );
    }, [id, label, labelHelp, darkLabel, largeLabel, required]);

    const getValue = useCallback(() => {
        const rawValue = inputElement.current?.value;

        if (replacer) {
            return replacer(rawValue);
        }

        return rawValue;
    }, [inputElement, replacer]);

    const focus = useCallback(() => {
        inputElement.current?.focus();
    }, [inputElement]);

    const handleChange = useCallback(event => {
        const value = getValue();

        setValueState(value);

        onChange && onChange(triggerAsEvent ? event : value);
    }, [getValue, triggerAsEvent, onChange]);

    const handleFocus = useCallback(() => {
        setFocused(true);

        onFocus && onFocus();
    }, [onFocus]);

    const handleBlur = useCallback(() => {
        setFocused(false);

        onBlur && onBlur(inputElement.current?.value);
    }, [onBlur, inputElement]);

    const handleKeyDown = useCallback(event => {
        if (blurOnEnter && event.which === 13) {
            event.preventDefault();
            inputElement.current?.blur();
        }

        onKeyDown && onKeyDown(event);
    }, [blurOnEnter, inputElement, onKeyDown]);

    const handleTogglePassword = useCallback(() => {
        setPasswordVisible(prevState => !prevState);
    }, []);

    useEffect(() => {
        if (autoFocus) {
            focus();
        }
    }, []);

    useEffect(() => {
        const ctrl = {
            focus: () => {
                focus();
            },
            getValue: () => {
                return valueState;
            },
            setValue: value => {
                setValueState(value);
            },
            isPopulated: () => {
                return populated;
            }
        };

        if (provideController) {
            if ('current' in provideController) {
                provideController.current = ctrl;
            } else {
                provideController && provideController(ctrl);
            }
        }

        return () => {
            if (provideController) {
                if ('current' in provideController) {
                    provideController.current = null;
                } else {
                    provideController && provideController(null);
                }
            }
        }
    }, [provideController, valueState, populated]);

    return (
        <div className="field-holder">
            { labelElement }

            <div className={'field field-default' + (disabled ? ' disabled' : '') + (populated ? ' populated' : '') + (focused ? ' focus' : '') + (shortField ? ' field-short' : '') + (error ? ' error' : '') + (small ? ' field-small' : '') + (large ? ' field-large' : '')} onClick={onClick}>

                { children }

                <input
                    className="field-input"
                    ref={inputElement}
                    type={passwordVisible ? 'tyext' : type}
                    readOnly={readonly}
                    name={name}
                    value={valueState}
                    id={id}
                    placeholder={placeholder}
                    onKeyDown={handleKeyDown}
                    onInput={onInput}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    onPaste={onPaste}
                    disabled={disabled}
                />

                {
                    sideAction ? (
                        <span className="field-side-action" onClick={onSideActionClick}>
                            { sideAction }
                        </span>
                    ) : null
                }

                {
                    passwordToggleable ? (
                        <span className="toggle-password" onClick={handleTogglePassword}>
                            <span className="hidden-state">{ t('show') }</span>

                            <span className="visible-state">{ t('hide') }</span>
                        </span>
                    ) : null
                }

                {
                    addon ? (
                        <span className={'field-input-addon' + (addonTransparent ? ' transparent' : '')} onClick={addonClick}>
                            { addon }
                        </span>
                    ) : null
                }

            </div>
        </div>
    );
}


export default FieldDefault;
